import {
  LayoutDashboard,
  FileSearch,
  GitCompareArrows,
  BarChart3,
  Menu,
  X,
  LogOut,
  ChevronDown,
  User,
} from 'lucide-react';
import { useState, useEffect, useRef } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';

import {
  requestSsoExchangeCode,
  buildSsoRedirectUrl,
  type SwitchableModule,
} from '@aia/platform-core';
import { AppSwitcher, cn } from '@aia/ui-components';

import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';

const APP_URLS: Partial<Record<SwitchableModule, string | undefined>> = {
  pilot: import.meta.env.VITE_PILOT_URL,
  progress: import.meta.env.VITE_PROGRESS_URL,
  insight: import.meta.env.VITE_INSIGHT_URL,
  admin: import.meta.env.VITE_ADMIN_URL,
};

async function switchToApp(target: SwitchableModule): Promise<void> {
  const base = APP_URLS[target];
  if (!base) return;
  const code = await requestSsoExchangeCode(api, target);
  window.location.href = buildSsoRedirectUrl(base, code);
}

interface NavItem {
  name: string;
  href: string;
  icon: typeof LayoutDashboard;
}

const NAV_ITEMS: NavItem[] = [
  { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
  { name: 'Assessments', href: '/assessments', icon: FileSearch },
  { name: 'Crosswalk', href: '/crosswalk', icon: GitCompareArrows },
  { name: 'Dashboard Views', href: '/dashboard-views', icon: BarChart3 },
];

const ROUTE_LABELS: Record<string, string> = {
  '/assessments': 'Assessments',
  '/assessments/upload': 'Upload Assessments',
  '/crosswalk': 'Crosswalks',
  '/dashboard-views': 'Dashboard Views',
  '/dashboard-views/new': 'Create Dashboard View',
};

function useCurrentPageLabel(pathname: string): string | null {
  if (pathname === '/dashboard') return null;

  const exact = ROUTE_LABELS[pathname];
  if (exact) return exact;

  for (const [route, label] of Object.entries(ROUTE_LABELS)) {
    if (pathname.startsWith(route)) return label;
  }
  return null;
}

export function DashboardLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const mainRef = useRef<HTMLElement>(null);
  const currentPage = useCurrentPageLabel(location.pathname);

  useEffect(() => {
    setSidebarOpen(false);
    mainRef.current?.focus({ preventScroll: true });
  }, [location.pathname]);

  const isActive = (href: string) => {
    if (href === '/dashboard') return location.pathname === '/dashboard';
    return location.pathname.startsWith(href);
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const getUserInitials = () => {
    if (!user?.name) return 'U';
    const parts = user.name.split(' ');
    if (parts.length >= 2 && parts[0] && parts[parts.length - 1]) {
      const lastPart = parts[parts.length - 1];
      return `${parts[0][0]}${lastPart?.[0] ?? ''}`.toUpperCase();
    }
    return user.name.substring(0, 2).toUpperCase();
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:z-[100] focus:p-2 focus:bg-white focus:text-black focus:shadow-lg"
      >
        Skip to main content
      </a>

      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          role="presentation"
          onClick={() => setSidebarOpen(false)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') setSidebarOpen(false);
          }}
        />
      )}

      {profileMenuOpen && (
        <div
          className="fixed inset-0 z-40"
          role="presentation"
          onClick={() => setProfileMenuOpen(false)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') setProfileMenuOpen(false);
          }}
        />
      )}

      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 w-64 bg-white border-r transform transition-transform duration-200 lg:translate-x-0',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full',
        )}
        aria-label="Main navigation"
      >
        <div className="h-16 flex items-center justify-between px-4 border-b">
          <Link to="/dashboard" className="flex items-center gap-2">
            <img src="/brand/logo-color.png" alt="Academics in Action" className="h-8 w-auto" />
            <span className="text-sm font-semibold text-foreground">InsightPulse</span>
          </Link>
          <button
            className="lg:hidden min-h-[44px] min-w-[44px] inline-flex items-center justify-center rounded-md hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary"
            onClick={() => setSidebarOpen(false)}
            aria-label="Close navigation menu"
          >
            <X className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>

        <nav className="mt-4 px-2" aria-label="Sidebar">
          <ul className="space-y-1">
            {NAV_ITEMS.map((item) => (
              <li key={item.name}>
                <Link
                  to={item.href}
                  className={cn(
                    'flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                    isActive(item.href)
                      ? 'bg-primary/10 text-foreground'
                      : 'text-gray-600 hover:bg-gray-100',
                  )}
                  aria-current={isActive(item.href) ? 'page' : undefined}
                  data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <item.icon
                    className={cn(
                      'h-5 w-5',
                      isActive(item.href) ? 'text-secondary' : 'text-gray-500',
                    )}
                    aria-hidden="true"
                  />
                  {item.name}
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-4 border-t bg-white">
          <div className="relative">
            <button
              className="flex items-center gap-3 w-full p-2 rounded-lg hover:bg-gray-100 transition-colors"
              onClick={() => setProfileMenuOpen(!profileMenuOpen)}
              aria-expanded={profileMenuOpen}
              aria-haspopup="true"
              aria-label="User profile menu"
              data-testid="user-menu"
            >
              <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center text-primary-foreground text-sm font-medium">
                {getUserInitials()}
              </div>
              <div className="flex-1 text-left min-w-0">
                <div className="text-sm font-medium truncate">{user?.name || 'User'}</div>
                <div className="text-xs text-gray-500">{user?.role}</div>
              </div>
              <ChevronDown
                className={cn(
                  'h-4 w-4 text-gray-500 transition-transform',
                  profileMenuOpen && 'rotate-180',
                )}
                aria-hidden="true"
              />
            </button>

            {profileMenuOpen && (
              <div className="absolute bottom-full left-0 right-0 mb-2 bg-white border rounded-lg shadow-lg py-1 z-50">
                <div className="px-3 py-2 border-b">
                  <div className="text-sm font-medium">{user?.name}</div>
                  <div className="text-xs text-gray-500">{user?.email}</div>
                </div>
                <button
                  onClick={() => {
                    setProfileMenuOpen(false);
                    navigate('/');
                  }}
                  className="flex items-center gap-2 w-full px-3 py-2 text-sm text-left hover:bg-gray-100 transition-colors"
                  aria-label="User profile"
                  data-testid="platform-button"
                >
                  <User className="h-4 w-4 text-gray-500" aria-hidden="true" />
                  Back to Platform
                </button>
                <button
                  onClick={handleLogout}
                  className="flex items-center gap-2 w-full px-3 py-2 text-sm text-left text-red-600 hover:bg-red-50 transition-colors"
                  aria-label="Log out"
                  data-testid="logout-button"
                >
                  <LogOut className="h-4 w-4" aria-hidden="true" />
                  Log Out
                </button>
              </div>
            )}
          </div>
        </div>
      </aside>

      <div className="lg:pl-64">
        <header className="h-16 bg-white border-b flex items-center justify-between px-4">
          <button
            className="lg:hidden min-h-[44px] min-w-[44px] inline-flex items-center justify-center rounded-md hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary"
            onClick={() => setSidebarOpen(true)}
            aria-label="Open navigation menu"
            aria-expanded={sidebarOpen}
          >
            <Menu className="h-6 w-6" aria-hidden="true" />
          </button>

          <div className="flex items-center gap-4 ml-auto">
            <AppSwitcher
              currentModule="insight"
              enabledModules={user?.enabledModules ?? []}
              isPlatformAdmin={user?.isPlatformAdmin}
              appUrls={APP_URLS}
              onSwitch={switchToApp}
            />
            <button
              onClick={handleLogout}
              className="inline-flex items-center gap-2 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
              aria-label="Log out"
              data-testid="header-logout-button"
            >
              <LogOut className="h-4 w-4" aria-hidden="true" />
              Log Out
            </button>
          </div>
        </header>

        {currentPage && (
          <div className="px-4 lg:px-6 pt-3 pb-0 bg-white border-b">
            <nav aria-label="Breadcrumb" className="mb-4">
              <ol className="flex items-center gap-2 text-sm text-muted-foreground">
                <li>
                  <Link to="/dashboard" className="hover:text-foreground transition-colors">
                    Dashboard
                  </Link>
                </li>
                <li aria-hidden="true">/</li>
                <li aria-current="page" className="text-foreground font-medium">
                  {currentPage}
                </li>
              </ol>
            </nav>
          </div>
        )}

        <main
          id="main-content"
          ref={mainRef}
          tabIndex={-1}
          className="p-4 lg:p-6 min-h-[calc(100vh-8rem)] outline-none"
        >
          <Outlet />
        </main>

        <footer className="py-4 px-6 border-t bg-white text-center text-sm text-gray-500">
          &copy; {new Date().getFullYear()} Academics in Action LLC. All rights reserved.
        </footer>
      </div>
    </div>
  );
}
